import {inject, Injectable} from '@angular/core';
import {firstValueFrom} from 'rxjs';
import {
  DemonstrationEvaluationControllerService,
  EvaluationCriteriaControllerService,
  ProjectRoleType,
  ProjectSettingControllerService,
  ProjectStepType,
  ProjectUserControllerService,
  ProjectUserDetailResponse,
  TenderPhaseType,
  ProjectDecisionControllerService,
  RfqControllerService,
  HrToolTotalFeesPerProviderResponse,
} from '@frontend/shared-api';
import {DirectusService} from '@frontend/directus';
import {buildEnrichedRfdEvaluationData} from './rfd-evaluation-data.service';
import {RfdScoreCalculationService} from './rfd-score-calculation.service';
import {
  ProviderTotalScoreCalculationService,
  ProviderTotalScoreResult,
} from './provider-total-score-calculation.service';
import {RfqProviderResultsService} from '../components/table/provider-results-table/services/rfq-provider-results.service';
import {CurrencyData} from '@frontend/shared';
import {isEC9} from '../constants/evaluation-criteria.constants';

export interface PhaseWeights {
  rfi: number;
  rfp: number;
  rfd: number;
  rfq: number;
}

export interface ToolPhaseScores {
  hrToolDirectusId: string;
  rfiPercentage: number;
  rfpPercentage: number;
  rfdPercentage: number;
  rfqPercentage: number;
  overallPercentage: number;
  totalCostThreeYears: number;
  currencyData: CurrencyData;
}

export interface OverallScoreResult {
  toolScores: Map<string, ToolPhaseScores>;
  phaseWeights: PhaseWeights;
  rfiTotals: Map<string, ProviderTotalScoreResult>;
  rfpTotals: Map<string, ProviderTotalScoreResult>;
  rfdPercentages: Map<string, number>;
}

@Injectable({
  providedIn: 'root',
})
export class OverallScoreService {
  private readonly providerTotalScoreService = inject(ProviderTotalScoreCalculationService);
  private readonly rfdScoreCalculationService = inject(RfdScoreCalculationService);
  private readonly evaluationService = inject(DemonstrationEvaluationControllerService);
  private readonly projectUserController = inject(ProjectUserControllerService);
  private readonly directusService = inject(DirectusService);
  private readonly projectSettingService = inject(ProjectSettingControllerService);
  private readonly rfqController = inject(RfqControllerService);
  private readonly projectDecisionController = inject(ProjectDecisionControllerService);
  private readonly rfqProviderResultsService = inject(RfqProviderResultsService);
  private readonly ecService = inject(EvaluationCriteriaControllerService);

  async loadOverallScores(projectId: number): Promise<OverallScoreResult> {
    const [rfiResult, rfpResult, rfdWeightResult, rfdPercentages, rfqData] = await Promise.all([
      this.providerTotalScoreService.calculateProviderTotalsWithWeight(
        projectId,
        TenderPhaseType.RFI,
      ),
      this.providerTotalScoreService.calculateProviderTotalsWithWeight(
        projectId,
        TenderPhaseType.RFP,
      ),
      this.providerTotalScoreService.calculateProviderTotalsWithWeight(
        projectId,
        TenderPhaseType.RFD,
      ),
      this.loadRfdPercentages(projectId),
      this.loadRfqData(projectId),
    ]);

    const phaseWeights: PhaseWeights = {
      rfi: rfiResult.totalWeight,
      rfp: rfpResult.totalWeight,
      rfd: rfdWeightResult.totalWeight,
      rfq: rfqData.weight,
    };

    const totalWeightSum =
      phaseWeights.rfi + phaseWeights.rfp + phaseWeights.rfd + phaseWeights.rfq;

    const allToolIds = new Set([
      ...rfiResult.totals.keys(),
      ...rfpResult.totals.keys(),
      ...rfdPercentages.keys(),
      ...rfqData.percentages.keys(),
    ]);

    const toolScores = new Map<string, ToolPhaseScores>();

    allToolIds.forEach((toolId) => {
      const rfiPercentage = rfiResult.totals.get(toolId)?.percentage ?? 0;
      const rfpPercentage = rfpResult.totals.get(toolId)?.percentage ?? 0;
      const rfdPercentage = rfdPercentages.get(toolId) ?? 0;
      const rfqPercentage = rfqData.percentages.get(toolId) ?? 0;

      const overallPercentage =
        totalWeightSum > 0
          ? (rfiPercentage * phaseWeights.rfi +
              rfpPercentage * phaseWeights.rfp +
              rfdPercentage * phaseWeights.rfd +
              rfqPercentage * phaseWeights.rfq) /
            totalWeightSum
          : 0;

      toolScores.set(toolId, {
        hrToolDirectusId: toolId,
        rfiPercentage,
        rfpPercentage,
        rfdPercentage,
        rfqPercentage,
        overallPercentage,
        totalCostThreeYears: rfqData.totalCosts.get(toolId) ?? 0,
        currencyData: rfqData.currencyDataByToolId.get(toolId) ?? {
          locale: 'de-DE',
          currencyCode: 'EUR',
        },
      });
    });

    return {
      toolScores,
      phaseWeights,
      rfiTotals: rfiResult.totals,
      rfpTotals: rfpResult.totals,
      rfdPercentages,
    };
  }

  async loadRfiRfpRfdScores(projectId: number): Promise<{
    rfiTotals: Map<string, ProviderTotalScoreResult>;
    rfpTotals: Map<string, ProviderTotalScoreResult>;
    rfdPercentages: Map<string, number>;
    rfiWeight: number;
    rfpWeight: number;
    rfdWeight: number;
  }> {
    const [rfiResult, rfpResult, rfdWeightResult, rfdPercentages] = await Promise.all([
      this.providerTotalScoreService.calculateProviderTotalsWithWeight(
        projectId,
        TenderPhaseType.RFI,
      ),
      this.providerTotalScoreService.calculateProviderTotalsWithWeight(
        projectId,
        TenderPhaseType.RFP,
      ),
      this.providerTotalScoreService.calculateProviderTotalsWithWeight(
        projectId,
        TenderPhaseType.RFD,
      ),
      this.loadRfdPercentages(projectId),
    ]);

    return {
      rfiTotals: rfiResult.totals,
      rfpTotals: rfpResult.totals,
      rfdPercentages,
      rfiWeight: rfiResult.totalWeight,
      rfpWeight: rfpResult.totalWeight,
      rfdWeight: rfdWeightResult.totalWeight,
    };
  }

  async loadRfdPercentages(projectId: number): Promise<Map<string, number>> {
    const [evaluationDataResponse, contributors, allSubcriteria, agenda, projectSettings] =
      await Promise.all([
        firstValueFrom(this.evaluationService.getEvaluationData({projectId})),
        firstValueFrom(this.projectUserController.getProjectUsersWithDetails({projectId})),
        firstValueFrom(this.directusService.loadSubCriteria()),
        firstValueFrom(this.directusService.loadDemonstrationAgenda()),
        firstValueFrom(this.projectSettingService.getProjectSetting({projectId})).catch(() => null),
      ]);

    const isExponential = Boolean(projectSettings?.isExponential);
    const contributorIds = this.getRfdContributorIds(contributors);
    const enrichedData = buildEnrichedRfdEvaluationData(
      evaluationDataResponse,
      allSubcriteria,
      agenda,
    );

    const scores = this.rfdScoreCalculationService.calculateProviderScores(
      enrichedData,
      contributorIds,
      isExponential,
    );

    return new Map(scores.map((score) => [score.hrToolDirectusId, score.percentage]));
  }

  private async loadRfqData(projectId: number): Promise<{
    percentages: Map<string, number>;
    weight: number;
    totalCosts: Map<string, number>;
    currencyDataByToolId: Map<string, CurrencyData>;
  }> {
    const [rfqTotals, rfqInvitationOverviews, ecs, projectSettings] = await Promise.all([
      firstValueFrom(this.projectDecisionController.getTotalFees({projectId})).catch(
        (): HrToolTotalFeesPerProviderResponse[] => [],
      ),
      firstValueFrom(this.rfqController.getInvitationOfferOverviews({projectId})).catch(() => []),
      firstValueFrom(this.ecService.getEvaluationCriteriaByProject({projectId})),
      firstValueFrom(this.projectSettingService.getProjectSetting({projectId})).catch(() => null),
    ]);

    const ec9 = ecs.find((ec) => isEC9(ec.ecNumber));
    const rfqWeight = ec9?.weight ?? 0;
    const isExponential = Boolean(projectSettings?.isExponential);

    const totalCosts = new Map<string, number>();
    const currencyDataByToolId = new Map<string, CurrencyData>();
    const providerToToolId = new Map<string, string>();

    rfqTotals.forEach((item) => {
      const toolId = item.providerTenantMappingResponse.hrToolDirectusId;
      totalCosts.set(toolId, item.hrToolTotalFees.totalAllYearsFees);
      currencyDataByToolId.set(toolId, {
        locale: item.locale,
        currencyCode: item.currencyCode,
      });
      providerToToolId.set(item.providerTenantMappingResponse.providerTenantId, toolId);
    });

    const percentagesByProviderId = this.rfqProviderResultsService.calculateRfqTotalPercentages(
      ec9,
      rfqInvitationOverviews,
      projectSettings ?? undefined,
      isExponential,
    );

    const percentages = new Map<string, number>();
    percentagesByProviderId.forEach((value, providerId) => {
      const toolId = providerToToolId.get(providerId);
      if (!toolId) return;
      percentages.set(toolId, value);
    });

    return {percentages, weight: rfqWeight, totalCosts, currencyDataByToolId};
  }

  private getRfdContributorIds(contributors: ProjectUserDetailResponse[]): number[] {
    return contributors
      .filter(
        (u) =>
          (u.roles.includes(ProjectRoleType.CONTRIBUTOR) &&
            u.accessibleSteps.includes(ProjectStepType.RFD_PROVIDER_EVALUATION)) ||
          u.roles.includes(ProjectRoleType.PROJECT_MANAGER),
      )
      .map((u) => u.userId);
  }
}
